import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import PricingModal from "./PricingModal";
import { useState } from "react";

interface InactiveStateHandlerProps {
  children: React.ReactNode;
}

const InactiveStateHandler = ({ children }: InactiveStateHandlerProps) => {
  const { user } = useAuth();
  const { createCheckout } = useSubscription();
  const [showPricing, setShowPricing] = useState(false);

  const { data: userCredits } = useQuery({
    queryKey: ['user-credits', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  // IMPL-5: Check if subscription should be inactive
  const isInactive = () => {
    if (!userCredits) return false;
    
    const now = new Date();
    const { subscription_status, trial_end_date, subscription_end_date } = userCredits;
    
    // Condition 1: Trial expired without payment
    if (subscription_status === 'trial' && trial_end_date && new Date(trial_end_date) < now) {
      return true;
    }
    
    // Condition 2: Subscription expired (canceled, past_due, etc.)
    if (['active', 'canceled'].includes(subscription_status) && subscription_end_date && new Date(subscription_end_date) < now) {
      return true;
    }
    
    return false;
  };

  const handleReactivate = () => {
    setShowPricing(true);
  };

  const handlePricingComplete = () => {
    setShowPricing(false);
  };

  if (isInactive()) {
    return (
      <>
        <div className="min-h-screen bg-app-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Subscription Inactive
            </h2>
            
            <p className="text-gray-600 mb-6">
              Your subscription has expired. Reactivate your plan to continue using AI features.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={handleReactivate}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Reactivate Plan
              </Button>
              
              <div className="text-sm text-gray-500">
                Credits remaining: {userCredits?.credits_left || 0}
              </div>
            </div>
          </div>
        </div>
        
        <PricingModal
          open={showPricing}
          onOpenChange={setShowPricing}
          onTrialComplete={handlePricingComplete}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default InactiveStateHandler;